/* eslint-disable camelcase */
import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import CloseIcon from '@material-ui/icons/Close'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import InputBase from '@material-ui/core/InputBase'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import Router from 'next/router'
import Card from '@components/card-medium'
import Pagination from '@components/pagination'
import ErrorBoundary from '@components/error/boundary'
import useStyles from './search.styles'
import { NextSeo } from 'next-seo'
import defaults from '@config/defaults'

const labels = {
  noResults: {
    title: 'Sorry, no result found.',
    desc: 'We couldn’t find what you are looking for. Try again.',
    placeholderSearch: 'Search for articles and events',
  },
  noTerm: {
    title: 'Search for articles and events above',
    desc: '',
    placeholderSearch: 'Search for articles and events',
  },
}

const determineCategory = (type, category) => {
  if (type === 'legacy_article') return 'archive'
  if (type === 'legacy_event_article') return 'archive'
  if (category === 'instruction') return 'instructional'
  if (type === 'contentful_story') return 'stories'
  if (type === 'contentful_event') return 'event'

  return category
}

const Search = (props) => {
  const {
    total,
    searchText,
    searchResults,
    offset,
    handlePagination,
  } = props
  const [valueSearch, setValueSearch] = useState(searchText)
  const [scrolled, setScrolled] = useState(false)
  const styles = useStyles({ scrolled })
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  const labelResult = searchText === '' ? 'noTerm' : 'noResults'

  const handleScroll = () => {
    if (isSm && window.pageYOffset > 70) {
      setScrolled(true)
    } else {
      setScrolled(false)
    }
  }

  useEffect(() => { setValueSearch(searchText) }, [searchText])

  useEffect(() => {
    document.addEventListener('scroll', handleScroll)
    return () => {
      document.addEventListener('scroll', handleScroll)
    }
  }, [isSm])
  const fixedTotal = total < 1000 ? total : 'Over 1000'

  const legend = total ? `${fixedTotal} results for "${searchText}"` : ''

  useEffect(() => {
    if (scrolled) { setValueSearch(legend) } else { setValueSearch(searchText) }
  }, [scrolled])

  const executeSearch = () => {
    if (valueSearch.length >= 3 && valueSearch !== legend) {
      Router.push(`/search?searchText=${valueSearch}&offset=0`)
      window.scrollTo(0, 0)
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') { executeSearch() }
  }

  const newArticles = searchResults ? searchResults.map(article => ({
    ...article,
    changed_date: article.created_date,
    cat: {
      series: determineCategory(article.object_type, article.category),
    },
  })) : null

  const noResults = () => (
    <Box align="center" m="auto" my={12}>
      <Typography variant="h4">{labels[labelResult].title}</Typography>
      <Typography paragraph>{labels[labelResult].desc}</Typography>
      <img src="/images/not-found.png" alt={labels[labelResult].titleNoRestuls} width={300} />
    </Box>
  )

  return (
    <div className={styles.root}>
      <NextSeo
        noindex={defaults.noindexNofollow}
        nofollow={defaults.noindexNofollow}
      />
      <Container>
        <div className={styles.containerInput}>
          <Box display="flex">
            {scrolled && (
              <IconButton onClick={executeSearch}>
                <SearchIcon />
              </IconButton>
            )}

            <InputBase
              fullWidth
              placeholder={labels[labelResult].placeholderSearch}
              value={valueSearch}
              className={styles.input}
              onKeyDown={handleKeyDown}
              onChange={e => setValueSearch(e.target.value)}
            />
            {(!searchResults || (valueSearch.trim() !== searchText && !scrolled)) ? (
              <IconButton onClick={executeSearch}>
                <SearchIcon />
              </IconButton>
            )
              : (
                <IconButton aria-label="search" onClick={() => setValueSearch('')}>
                  <CloseIcon />
                </IconButton>
              )}
          </Box>
          {!scrolled && <Typography variant="caption" className={styles.legend}>{legend}</Typography>}
        </div>

        <Grid container spacing={2} className={`${styles.grid} ${(total <= 36) && styles.pagination}`}>
          {newArticles ? newArticles.map(card => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={card.object_id}>
              <ErrorBoundary>
                <Card {...card} />
              </ErrorBoundary>
            </Grid>
          )) : noResults()}
        </Grid>
        {newArticles && (total > 36) && (
          <ErrorBoundary>
            <Pagination
              className={styles.pagination}
              limit={36}
              offset={offset}
              total={total < 1000 ? total : 1000}
              onClick={(e, newOffset) => handlePagination(newOffset)}
              padding={[4, 6, 8]}
            />
          </ErrorBoundary>
        )}
      </Container>
    </div>
  )
}

Search.propTypes = {
  total: PropTypes.number,
  searchText: PropTypes.string,
  searchResults: PropTypes.arrayOf(PropTypes.shape({
    object_type: PropTypes.string,
    headline: PropTypes.string,
    slug: PropTypes.string,
    url: PropTypes.string,
  })),
  offset: PropTypes.string,
  handlePagination: PropTypes.func,
}

export default Search
